import { type ReportTemplateId } from 'shared/api/standardReports'
import { type ConfigStatus, type ErrorCodeType } from 'shared/configStatuses'

/**
 * Cirrus data sources the Flow Copilot agent can be pointed at directly.
 *
 * Flow reads `state.cirrus_source` off the AG-UI `RunAgentInput` and queries Cirrus
 * itself, so Alvie never has to sync a Flow entity first — the collection and config id
 * Flow needs already live in Alvie's own database.
 *
 * Alvie names the source; Flow's agent picks the report. No report id is ever sent, so
 * Alvie never has to know Flow's report vocabulary — a catalog that has already drifted
 * from Alvie's own, and where a stale id is a hard `400` that kills the turn.
 *
 * `standard_reports` is the exception that needs narrowing: it spans 16 reports across five
 * unrelated product areas, so a `kind` scopes the agent to the one the user is looking at.
 */
export const CIRRUS_COLLECTIONS = ['event_feed', 'standard_reports', 'attribution', 'bo', 'enhanced_signals'] as const

export type CirrusCollection = (typeof CIRRUS_COLLECTIONS)[number]

/**
 * Sub-area of `standard_reports`, which is far wider than the other collections. `kind`
 * *restricts* what the agent may query — that is the point: it stops the agent wandering
 * into product-mix data when the user is looking at creative, or reaching for a report the
 * client's config never provisioned.
 *
 * The key is Alvie's own reporting-solution template id, so there is no third vocabulary to
 * agree on and drift: a solution config carries exactly one template, and that template id
 * is the `kind`. Flow keys its `source_kinds` registry by the same ids.
 *
 * Only valid on `standard_reports`; sending one with any other collection is a `400`.
 */
export const CIRRUS_STANDARD_REPORT_KINDS = [
  'reporting_base',
  'creative_insights_report',
  'pmm_report',
  'search_synergies_report',
  'arima_plus_report'
] as const satisfies readonly ReportTemplateId[]

export type CirrusStandardReportKind = (typeof CIRRUS_STANDARD_REPORT_KINDS)[number]

export const isCirrusStandardReportKind = (value: string): value is CirrusStandardReportKind =>
  (CIRRUS_STANDARD_REPORT_KINDS as readonly string[]).includes(value)

/** Shape written to `RunAgentInput.state.cirrus_source`. Snake case — this is Flow's wire format. */
export interface CirrusSourceState {
  collection: CirrusCollection
  kind?: CirrusStandardReportKind
  config_id?: string
  client_id?: number
  name?: string
}

/** A Cirrus data source the user can pick, resolved from Alvie's own config lists. */
export interface CirrusSourceOption {
  /** Menu option id — `cirrus:<collection>:<configId>`. */
  id: string
  collection: CirrusCollection
  /** `null` for config-less collections (Recommendations). */
  configId: string | null
  /** Sub-area of `standard_reports` this source covers. Unset for every other collection. */
  kind?: CirrusStandardReportKind
  /**
   * Exact label shown in the picker and selected chip; sent as `name` so it appears in
   * the agent's answers. For reporting solutions this is `descriptiveName` (not the
   * technical `name` / table suffix).
   */
  label: string
  /** Grey secondary line under the label (e.g. reporting-solution `templateId`). */
  annotation?: string
  /** `false` renders a disabled "coming soon" row — the agent cannot answer on this source yet. */
  supported?: boolean
  /** Pipeline status of the underlying config, shown as a badge + tooltip in the picker. */
  status?: ConfigStatus
  /** Raw status detail, so the picker tooltip can match the one on the config's list view. */
  statusDetail?: {
    lastUpdated?: string
    errorCode?: ErrorCodeType | null
    errorMessage?: string | null
    targetProjectId?: string | null
  }
}

export const CIRRUS_OPTION_ID_PREFIX = 'cirrus'

export const getCirrusOptionId = (collection: CirrusCollection, configId: string | null): string =>
  `${CIRRUS_OPTION_ID_PREFIX}:${collection}:${configId ?? ''}`

export const isCirrusOptionId = (optionId: string): boolean => optionId.startsWith(`${CIRRUS_OPTION_ID_PREFIX}:`)

export const isCirrusCollection = (value: string): value is CirrusCollection =>
  (CIRRUS_COLLECTIONS as readonly string[]).includes(value)

/**
 * Turn a picked option into the `cirrus_source` payload.
 *
 * Returns `null` for anything Flow would reject with a `400` (unknown collection), so
 * a bad option can never poison every turn of the conversation.
 *
 * `client_id` is sent whenever it is a valid number: Flow treats the `X-Alvie-Client-Id`
 * header as the authority and `400`s on a mismatch, which is exactly the loud failure we
 * want if the picker ever drifts from the active client.
 */
export const buildCirrusSourceState = (
  option: CirrusSourceOption | null,
  clientId: number | null
): CirrusSourceState | null => {
  if (option == null || !isCirrusCollection(option.collection)) {
    return null
  }

  return {
    collection: option.collection,
    // `kind` is only valid on standard_reports — sending it elsewhere is a 400, so it is
    // gated on the collection rather than trusted to be set correctly upstream.
    ...(option.collection === 'standard_reports' && option.kind != null ? { kind: option.kind } : {}),
    // Config-less collections must not carry a config id — Recommendations has none.
    ...(option.configId != null && option.configId !== '' ? { config_id: option.configId } : {}),
    // `isSafeInteger`, not `isInteger`: Alvie client ids are 16-digit Datastore ids, and one
    // past 2^53 loses precision in `Number()`. Sending a silently-rounded id would mismatch
    // the header and 400 every turn, so omit it and let Flow use the header it trusts anyway.
    ...(clientId != null && Number.isSafeInteger(clientId) ? { client_id: clientId } : {}),
    ...(option.label !== '' ? { name: option.label } : {})
  }
}

export const areCirrusSourceStatesEqual = (
  left: CirrusSourceState | null,
  right: CirrusSourceState | null
): boolean => {
  if (left == null || right == null) {
    return left == null && right == null
  }
  return (
    left.collection === right.collection &&
    left.kind === right.kind &&
    left.config_id === right.config_id &&
    left.client_id === right.client_id &&
    left.name === right.name
  )
}

export const readAgentCirrusSource = (agentState: unknown): CirrusSourceState | null => {
  if (agentState == null || typeof agentState !== 'object') {
    return null
  }
  const cirrusSource = (agentState as Record<string, unknown>).cirrus_source
  if (cirrusSource == null || typeof cirrusSource !== 'object') {
    return null
  }
  return cirrusSource as CirrusSourceState
}
